import type { FC, ReactNode } from 'react'

import { useState } from 'react'
import { cn } from '@/lib/utils'
import { Reveal } from '@/components/generals/reveal'
import { PillButton } from '@/components/generals/pill-button'
import { useContact } from '@/components/generals/contact-dialog'
import { faqs } from '@/constants/full-service'

export const Faq: FC = (): ReactNode => {
    const [open, setOpen] = useState<number | null>(0)
    const { openContact } = useContact()

    return (
        <section id='faq' aria-labelledby='faq-title' className='border-t border-hairline bg-[#fafafb] py-24 md:py-32'>
            <div className='fc-container grid gap-12 lg:grid-cols-[minmax(0,380px)_minmax(0,1fr)] lg:gap-20'>
                <Reveal className='flex flex-col gap-5 lg:sticky lg:top-28 lg:self-start'>
                    <h2 id='faq-title' className='text-[clamp(2.25rem,4.2vw,3.5rem)] font-medium leading-[1.05] tracking-[-0.04em] text-ink'>
                        Frequently asked questions
                    </h2>

                    <p className='max-w-[360px] text-[17px] leading-relaxed text-slate'>
                        What to expect when FlowCraft builds and runs your integrations.
                    </p>
                </Reveal>

                <Reveal delay={100}>
                    <div className='border-t border-hairline'>
                        {faqs.map((item, index) => {
                            const isOpen = open === index
                            const isLast = index === faqs.length - 1

                            return (
                                <div key={item.question} className='border-b border-hairline'>
                                    <h3>
                                        <button
                                            type='button'
                                            id={`faq-trigger-${index}`}
                                            aria-expanded={isOpen}
                                            aria-controls={`faq-panel-${index}`}
                                            onClick={() => setOpen(isOpen ? null : index)}
                                            className='group flex w-full items-center justify-between gap-6 py-6 text-left text-[17px] font-medium tracking-[-0.015em] text-ink focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-violet md:text-[18px]'
                                        >
                                            <span>{item.question}</span>

                                            <span
                                                aria-hidden='true'
                                                className={cn(
                                                    'relative inline-flex size-8 shrink-0 items-center justify-center rounded-full ring-1 ring-inset transition-colors duration-300',
                                                    isOpen ? 'bg-ink ring-ink' : 'bg-white ring-[#e3e3e9] group-hover:ring-[#cfcfd8]'
                                                )}
                                            >
                                                <span className={cn('absolute h-[1.5px] w-3 rounded-full transition-colors duration-300', isOpen ? 'bg-white' : 'bg-ink')} />
                                                <span className={cn('absolute h-3 w-[1.5px] rounded-full transition-[rotate,background-color] duration-300 ease-out', isOpen ? 'rotate-90 bg-white' : 'rotate-0 bg-ink')} />
                                            </span>
                                        </button>
                                    </h3>

                                    <div
                                        id={`faq-panel-${index}`}
                                        role='region'
                                        aria-labelledby={`faq-trigger-${index}`}
                                        className={cn('grid transition-[grid-template-rows,opacity] duration-300 ease-out', isOpen ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0')}
                                    >
                                        <div className='overflow-hidden' inert={!isOpen}>
                                            <p className='max-w-[680px] pb-6 pr-10 text-[15.5px] leading-relaxed text-slate'>
                                                {item.answer}
                                            </p>

                                            {isLast && (
                                                <div className='pb-7'>
                                                    <PillButton size='sm' chevron onClick={() => openContact({ intent: 'demo', message: 'I would like to book a discovery call.' })}>
                                                        Book a discovery call
                                                    </PillButton>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )
                        })}
                    </div>
                </Reveal>
            </div>
        </section>
    )
}
